import { ScriptElementType, ELEMENT_TYPE_LABELS, LANE_COLOR_PALETTE } from '../../types/script'
import type {
  ScriptElementFormState,
  ScriptLane,
  LaneBranchType,
  TagPickerDto,
  QuestionPickerDto,
} from '../../types/script'
import { uuid } from '../../lib/uuid'

/**
 * Reconstruct lane layout from a flat list of elements.
 *
 * Algorithm:
 * 1. Build the main flow by following DisplayOrder (skipping elements claimed by branches)
 * 2. For each branch element in the main flow, follow branch targets to build branch lanes
 * 3. Detect merge points where branch elements' nextElementClientId points back to the main flow
 */
export function flatToLanes(
  elements: ScriptElementFormState[],
  options?: {
    tagOptions?: TagPickerDto[]
    questionOptions?: QuestionPickerDto[]
    questionAnswersCache?: Record<string, Array<{ id: string; answerText: string }>>
  }
): ScriptLane[] {
  if (elements.length === 0) {
    return [createMainLane([])]
  }

  const sorted = [...elements].sort((a, b) => a.displayOrder - b.displayOrder)
  const elementMap = new Map<string, ScriptElementFormState>()
  for (const el of sorted) {
    elementMap.set(el.clientId, el)
  }

  // Collect all branch target clientIds (elements that are first-in-line for a branch)
  const branchTargets = new Map<string, { sourceClientId: string; branchType: LaneBranchType; branchKey: string; label: string }>()

  for (const el of sorted) {
    collectBranchTargets(el, elementMap, branchTargets, options)
  }

  // Build main flow: elements NOT claimed as branch targets, in DisplayOrder
  const branchStartIds = new Set(branchTargets.keys())
  const claimedByBranch = new Set<string>()

  // Walk each branch chain to find all elements belonging to branches
  for (const startId of branchStartIds) {
    walkChain(startId, elementMap, branchStartIds, claimedByBranch)
  }

  const mainElements = sorted.filter((el) => !claimedByBranch.has(el.clientId))
  const lanes: ScriptLane[] = [createMainLane(mainElements)]

  // Build branch lanes
  let colorIdx = 0
  for (const [startId, info] of branchTargets) {
    const branchElements: ScriptElementFormState[] = []
    let mergeTarget: string | undefined

    // Walk the branch chain
    let currentId: string | undefined = startId
    const visited = new Set<string>()
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId)
      const el = elementMap.get(currentId)
      if (!el) break

      // If this element is in the main flow, it's a merge point — don't include it
      if (!claimedByBranch.has(el.clientId) && currentId !== startId) {
        mergeTarget = el.clientId
        break
      }

      // If this element IS the branch start but is in main flow,
      // it means the branch target points to an existing main flow element (a goto, not a new lane)
      if (!claimedByBranch.has(el.clientId) && currentId === startId) {
        // This is a "go to existing step" reference, not a real branch lane
        break
      }

      branchElements.push(el)

      // Follow the chain: nextElementClientId, then fall through by DisplayOrder within branch
      if (el.nextElementClientId) {
        const nextEl = elementMap.get(el.nextElementClientId)
        if (nextEl && !claimedByBranch.has(nextEl.clientId)) {
          // Next element is in main flow = merge point
          mergeTarget = nextEl.clientId
          break
        }
        if (nextEl && branchStartIds.has(nextEl.clientId)) {
          // Next element starts another branch lane — rejoin it rather than
          // absorbing its elements (which would put them in two lanes).
          mergeTarget = nextEl.clientId
          break
        }
        currentId = el.nextElementClientId
      } else {
        // Fall through to next element by DisplayOrder that is also in the branch
        const nextInOrder = findNextInBranch(el, sorted, claimedByBranch, visited)
        if (nextInOrder) {
          currentId = nextInOrder.clientId
        } else {
          break
        }
      }
    }

    if (branchElements.length > 0) {
      lanes.push({
        id: uuid(),
        label: info.label,
        colorScheme: LANE_COLOR_PALETTE[colorIdx % LANE_COLOR_PALETTE.length],
        elements: branchElements,
        sourceElementClientId: info.sourceClientId,
        sourceBranchType: info.branchType,
        sourceBranchKey: info.branchKey,
        mergeTargetClientId: mergeTarget,
      })
      colorIdx++
    }
  }

  return lanes
}

/**
 * Flatten lanes back to a flat element list for saving.
 *
 * 1. Main lane elements get sequential displayOrder starting at 0
 * 2. Branch lane elements follow, each lane in order
 * 3. Last element of each branch lane gets nextElementClientId = mergeTarget
 * 4. Branch source elements get their branch pointers updated to point to first element of each lane
 */
export function lanesToFlat(lanes: ScriptLane[]): ScriptElementFormState[] {
  const allElements: ScriptElementFormState[] = []
  let displayOrder = 0

  // Index lanes by source for quick lookup
  const lanesBySource = new Map<string, ScriptLane[]>()
  for (const lane of lanes) {
    if (lane.sourceElementClientId) {
      const existing = lanesBySource.get(lane.sourceElementClientId) || []
      existing.push(lane)
      lanesBySource.set(lane.sourceElementClientId, existing)
    }
  }

  // Main lane first — link consecutive elements via nextElementClientId
  const mainLane = lanes.find((l) => l.id === 'main')
  if (mainLane) {
    for (let i = 0; i < mainLane.elements.length; i++) {
      const el = { ...mainLane.elements[i], displayOrder: displayOrder++ }
      if (i < mainLane.elements.length - 1) {
        el.nextElementClientId = mainLane.elements[i + 1].clientId
      } else {
        el.nextElementClientId = undefined
      }
      allElements.push(el)
    }
  }

  // Branch lanes next
  for (const lane of lanes) {
    if (lane.id === 'main') continue
    for (let i = 0; i < lane.elements.length; i++) {
      const el = { ...lane.elements[i], displayOrder: displayOrder++ }

      // Set nextElementClientId to chain elements within the branch
      if (i < lane.elements.length - 1) {
        // Intermediate elements: point to next element in same lane
        el.nextElementClientId = lane.elements[i + 1].clientId
      } else if (lane.mergeTargetClientId) {
        // Last element: point to merge target if configured
        el.nextElementClientId = lane.mergeTargetClientId
      }
      // If last element with no merge target: nextElementClientId remains undefined (branch ends script)

      allElements.push(el)
    }
  }

  // Update branch source elements to point to first element of their lanes
  for (const el of allElements) {
    const childLanes = lanesBySource.get(el.clientId)
    if (!childLanes || childLanes.length === 0) continue

    for (const childLane of childLanes) {
      if (childLane.elements.length === 0) continue
      const firstChildId = childLane.elements[0].clientId

      if (el.elementType === ScriptElementType.SurveyQuestion) {
        // Answer branch: sourceBranchKey is the answerId
        if (childLane.sourceBranchType === 'answer' && childLane.sourceBranchKey) {
          el.answerBranches = {
            ...(el.answerBranches || {}),
            [childLane.sourceBranchKey]: firstChildId,
          }
        }
      } else if (el.elementType === ScriptElementType.ConditionalBranch) {
        if (childLane.sourceBranchType === 'true') {
          el.nextElementOnTrueClientId = firstChildId
        } else if (childLane.sourceBranchType === 'false') {
          el.nextElementOnFalseClientId = firstChildId
        } else if (childLane.sourceBranchType === 'prompt-response' && childLane.sourceBranchKey) {
          // Prompt response: sourceBranchKey is the response label
          if (el.promptResponses) {
            el.promptResponses = el.promptResponses.map((pr) =>
              pr.label === childLane.sourceBranchKey
                ? { ...pr, targetClientId: firstChildId }
                : pr
            )
          }
        }
      }
    }
  }

  return allElements
}

/**
 * Create a fresh element for the builder. Flagged isNew so its card opens expanded.
 */
export function createNewElement(type: ScriptElementType): ScriptElementFormState {
  return {
    clientId: uuid(),
    elementType: type,
    displayOrder: 0,
    isNew: true,
  }
}

/**
 * Create a default empty main lane.
 */
export function createMainLane(elements: ScriptElementFormState[] = []): ScriptLane {
  return {
    id: 'main',
    label: 'Main Flow',
    colorScheme: 'blue',
    elements,
  }
}

// ========== Internal Helpers ==========

function collectBranchTargets(
  el: ScriptElementFormState,
  elementMap: Map<string, ScriptElementFormState>,
  branchTargets: Map<string, { sourceClientId: string; branchType: LaneBranchType; branchKey: string; label: string }>,
  options?: {
    tagOptions?: TagPickerDto[]
    questionOptions?: QuestionPickerDto[]
    questionAnswersCache?: Record<string, Array<{ id: string; answerText: string }>>
  }
) {
  if (el.elementType === ScriptElementType.SurveyQuestion && el.answerBranches) {
    for (const [answerId, targetClientId] of Object.entries(el.answerBranches)) {
      if (targetClientId && elementMap.has(targetClientId)) {
        // Try to look up the answer text
        let label = 'Answer Branch'
        if (el.surveyQuestionId && options?.questionAnswersCache?.[el.surveyQuestionId]) {
          const answer = options.questionAnswersCache[el.surveyQuestionId].find(a => a.id === answerId)
          if (answer) {
            label = answer.answerText
          }
        }

        branchTargets.set(targetClientId, {
          sourceClientId: el.clientId,
          branchType: 'answer',
          branchKey: answerId,
          label,
        })
      }
    }
  }

  if (el.elementType === ScriptElementType.ConditionalBranch) {
    if (el.nextElementOnTrueClientId && elementMap.has(el.nextElementOnTrueClientId)) {
      let typeLabel = 'Match'
      if (el.conditionalField === 'Tag' && el.tagId && options?.tagOptions) {
        const tag = options.tagOptions.find(t => t.id === el.tagId)
        typeLabel = tag ? `Has ${tag.label}` : 'Has Tag'
      } else if (el.conditionalField === 'Tag') {
        typeLabel = 'Has Tag'
      }

      branchTargets.set(el.nextElementOnTrueClientId, {
        sourceClientId: el.clientId,
        branchType: 'true',
        branchKey: 'true',
        label: typeLabel,
      })
    }
    if (el.nextElementOnFalseClientId && elementMap.has(el.nextElementOnFalseClientId)) {
      let typeLabel = 'No Match'
      if (el.conditionalField === 'Tag' && el.tagId && options?.tagOptions) {
        const tag = options.tagOptions.find(t => t.id === el.tagId)
        typeLabel = tag ? `No ${tag.label}` : 'No Tag'
      } else if (el.conditionalField === 'Tag') {
        typeLabel = 'No Tag'
      }

      branchTargets.set(el.nextElementOnFalseClientId, {
        sourceClientId: el.clientId,
        branchType: 'false',
        branchKey: 'false',
        label: typeLabel,
      })
    }

    if (el.promptResponses) {
      for (const pr of el.promptResponses) {
        if (pr.targetClientId && elementMap.has(pr.targetClientId)) {
          branchTargets.set(pr.targetClientId, {
            sourceClientId: el.clientId,
            branchType: 'prompt-response',
            branchKey: pr.label,
            label: pr.label,
          })
        }
      }
    }
  }
}

/**
 * Walk a branch chain from startId, collecting all elements that belong to this branch.
 */
function walkChain(
  startId: string,
  elementMap: Map<string, ScriptElementFormState>,
  branchStartIds: Set<string>,
  claimedByBranch: Set<string>
) {
  let currentId: string | undefined = startId
  const visited = new Set<string>()
  const startEl = elementMap.get(startId)
  if (!startEl) return

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId)
    const el = elementMap.get(currentId)
    if (!el) break

    claimedByBranch.add(el.clientId)

    if (el.nextElementClientId) {
      const nextEl = elementMap.get(el.nextElementClientId)
      if (!nextEl) break

      // Stop if next element is a different branch start (not the current branch)
      if (branchStartIds.has(nextEl.clientId) && nextEl.clientId !== startId) {
        break
      }

      // Stop if next element merges back to main flow (lower displayOrder than branch start)
      if (nextEl.displayOrder < startEl.displayOrder) {
        break
      }

      // Stop if self-referencing
      if (nextEl.clientId === currentId) {
        break
      }

      // Continue following the chain
      currentId = el.nextElementClientId
    } else {
      // No explicit next — with the lanesToFlat fix, this shouldn't happen
      // but keep for safety (end of branch)
      break
    }
  }
}

function findNextInBranch(
  current: ScriptElementFormState,
  sorted: ScriptElementFormState[],
  claimedByBranch: Set<string>,
  visited: Set<string>
): ScriptElementFormState | null {
  const currentIdx = sorted.findIndex((e) => e.clientId === current.clientId)
  for (let i = currentIdx + 1; i < sorted.length; i++) {
    const candidate = sorted[i]
    if (claimedByBranch.has(candidate.clientId) && !visited.has(candidate.clientId)) {
      return candidate
    }
  }
  return null
}

/**
 * Get a summary label for an element (used in compact card view and merge target selectors).
 */
export function getElementSummary(
  el: ScriptElementFormState,
  options?: {
    tagOptions?: Array<{ id: string; label: string }>
    questionOptions?: Array<{ id: string; label: string; question: string }>
  }
): string {
  switch (el.elementType) {
    case ScriptElementType.Text:
      return el.textContent?.slice(0, 50) || '(empty text)'
    case ScriptElementType.SurveyQuestion:
      if (el.surveyQuestionId && options?.questionOptions) {
        const question = options.questionOptions.find(q => q.id === el.surveyQuestionId)
        if (question) {
          return question.label || question.question.slice(0, 50)
        }
      }
      return el.surveyQuestionId ? 'Survey Question' : '(no question selected)'
    case ScriptElementType.TagAssignment:
      if (el.tagId && options?.tagOptions) {
        const tag = options.tagOptions.find(t => t.id === el.tagId)
        if (tag) {
          return tag.label
        }
      }
      return el.tagId ? 'Tag Assignment' : '(no tag selected)'
    case ScriptElementType.ConditionalBranch:
      return el.conditionalField
        ? `Condition: ${el.conditionalField}`
        : '(no condition set)'
    default:
      return 'Unknown'
  }
}

/**
 * Find all elements across all lanes (flat helper for cross-lane references).
 */
export function getAllElements(lanes: ScriptLane[]): ScriptElementFormState[] {
  return lanes.flatMap((lane) => lane.elements)
}

/**
 * Find which lane an element belongs to.
 */
export function findLaneForElement(
  lanes: ScriptLane[],
  clientId: string
): ScriptLane | undefined {
  return lanes.find((lane) => lane.elements.some((el) => el.clientId === clientId))
}

/**
 * Get the step number for an element across all lanes (for display as "Step N").
 */
export function getStepNumber(lanes: ScriptLane[], clientId: string): number | null {
  let step = 1
  for (const lane of lanes) {
    for (const el of lane.elements) {
      if (el.clientId === clientId) return step
      step++
    }
  }
  return null
}

/**
 * Get the next available color from the palette based on existing lanes.
 */
export function getNextLaneColor(lanes: ScriptLane[]): string {
  const usedColors = new Set(lanes.map((l) => l.colorScheme))
  for (const color of LANE_COLOR_PALETTE) {
    if (!usedColors.has(color)) return color
  }
  return LANE_COLOR_PALETTE[lanes.length % LANE_COLOR_PALETTE.length]
}

/**
 * Get a label describing the source element of a branch lane (e.g., "Step 2 (Survey Question)").
 */
export function getSourceElementLabel(
  lanes: ScriptLane[],
  sourceClientId: string
): string {
  for (const lane of lanes) {
    for (const el of lane.elements) {
      if (el.clientId === sourceClientId) {
        const step = getStepNumber(lanes, sourceClientId)
        return `Step ${step} (${ELEMENT_TYPE_LABELS[el.elementType]})`
      }
    }
  }
  return 'Unknown'
}

/**
 * Find all branch lanes that merge into a given element.
 */
export function getMergingLanes(
  lanes: ScriptLane[],
  targetClientId: string
): ScriptLane[] {
  return lanes.filter((l) => l.mergeTargetClientId === targetClientId)
}

/**
 * Walk a branch's source chain up to the main-flow element its branch tree
 * originates from, returning that element's index within the main lane (or -1
 * if the chain never resolves to a main-flow element). A branch nested inside
 * other branches still ultimately descends from one main-flow element; that
 * element's position anchors which later main-flow steps it may rejoin.
 */
function getMainFlowOriginIndex(lanes: ScriptLane[], branch: ScriptLane): number {
  const mainLane = lanes.find((l) => l.id === 'main')
  if (!mainLane) return -1
  const seen = new Set<string>()
  let sourceId = branch.sourceElementClientId
  while (sourceId && !seen.has(sourceId)) {
    seen.add(sourceId)
    const mainIndex = mainLane.elements.findIndex((el) => el.clientId === sourceId)
    if (mainIndex !== -1) return mainIndex
    const lane = findLaneForElement(lanes, sourceId)
    if (!lane || lane.id === 'main') return -1
    sourceId = lane.sourceElementClientId
  }
  return -1
}

/**
 * Potential rejoin targets for a branch lane: the main-flow elements that come
 * after the main-flow element the branch tree originates from. Works at any
 * nesting depth — a deeply-nested branch can still rejoin the main flow after
 * its root ancestor.
 *
 * A naive comparison of `getStepNumber` values fails for nested branches:
 * getStepNumber counts elements across ALL lanes, so a nested source's global
 * step number is inflated past its legitimately-downstream main-flow targets,
 * which then get filtered out and the branch is stuck at "End of script".
 * Anchoring on the origin's main-flow position avoids that (and is equivalent
 * to the old behavior for non-nested branches, whose source IS a main element).
 */
export function getRejoinTargets(
  lanes: ScriptLane[],
  branch: ScriptLane
): { element: ScriptElementFormState; stepNumber: number | null }[] {
  const mainLane = lanes.find((l) => l.id === 'main')
  if (!mainLane || !branch.sourceElementClientId) return []
  const originIndex = getMainFlowOriginIndex(lanes, branch)
  if (originIndex === -1) return []
  return mainLane.elements
    .filter((_, idx) => idx > originIndex)
    .map((element) => ({ element, stepNumber: getStepNumber(lanes, element.clientId) }))
}
